export type AppLanguageCode = 'en' | 'sw' | 'ts' | 'fr';

type ResultCopy = {
  headline: string;
  explanation: string;
  suggestedAction: string;
};

export type AppCopy = {
  languageNames: Record<AppLanguageCode, {label: string; helper: string}>;
  landing: {
    eyebrow: string;
    subtitle: string;
    privacyTitle: string;
    privacyText: string;
    chooseLanguage: string;
    start: string;
    continue: string;
    providerView: string;
    urgentHelp: string;
    learnMore: string;
    credibilityCue: string;
    privacyReassurance: string;
    footer: string;
  };
  accessibility: {
    title: string;
    subtitle: string;
    highContrast: string;
    highContrastOn: string;
    highContrastOff: string;
    textSize: string;
    textSizeStandard: string;
    textSizeLarge: string;
    textSizeExtraLarge: string;
    selected: string;
    selectLanguageHint: string;
    textSizeHint: string;
  };
  clinicianLanding: {
    tagline: string;
    credibilityCue: string;
    screening: string;
    training: string;
    analytics: string;
    customize: string;
    resumeTitle: string;
    resumeBody: string;
    cancel: string;
    startNew: string;
    continue: string;
  };
  clinicalPrivacy: {
    title: string;
    subtitle: string;
    privateTitle: string;
    bullets: string[];
    continue: string;
    sharingNotice: string;
  };
  clinicalOrientation: {
    title: string;
    subtitle: string;
    bullets: string[];
    start: string;
  };
  privacy: {
    eyebrow: string;
    title: string;
    subtitle: string;
    privateTitle: string;
    bullets: string[];
    note: string;
    continue: string;
  };
  orientation: {
    eyebrow: string;
    title: string;
    subtitle: string;
    bullets: string[];
    privacy: string;
    start: string;
  };
  questions: {
    title: string;
    pauseTitle: string;
    pauseMessage: string;
    saveAndLeave: string;
    stay: string;
    restoring: string;
    recommending: string;
    privacy: string;
    firstQuestion: string;
    answered: string;
    adaptive: string;
    progressNote: string;
    sensitiveTitle: string;
    sensitiveBody: string;
    questionLabel: string;
    continue: string;
    pause: string;
    enoughTitle: string;
    enoughBody: string;
    noMoreQuestions: string;
    finish: string;
    loadingModels: string;
    chooseAnswer: string;
    invalidAnswer: string;
    failedAnswer: string;
    savedTitle: string;
    savedBody: string;
    backToStart: string;
    urgentTitle: string;
    urgentBody: string;
    seeSupport: string;
  };
  results: {
    eyebrow: string;
    show: string;
    hide: string;
    hiddenTitle: string;
    hiddenBody: string;
    loading: string;
    nextStep: string;
    disclaimer: string;
    getSupportNow: string;
    seeSupport: string;
    backToStart: string;
    clinical: {
      title: string;
      conditionIndicators: string;
      overallSeverity: string;
      clinicalNote: string;
      careGuidelines: string;
      advisory: string;
      indicatorLevels: {
        moderate: string;
        mild: string;
        low: string;
      };
      overallLevels: {
        doingOkay: string;
        someStress: string;
        difficultTime: string;
        extraSupport: string;
        urgent: string;
      };
      noteTemplates: {
        doingOkay: string;
        someStress: string;
        difficultTime: string;
        extraSupport: string;
        urgent: string;
      };
    };
  };
  support: {
    eyebrow: string;
    title: string;
    subtitle: string;
    urgentTitle: string;
    urgentBody: string;
    options: {title: string; description: string; urgent?: boolean}[];
    backToStart: string;
  };
  resultSummaries: Record<
    'doing_okay' | 'some_stress' | 'difficult_time' | 'extra_support' | 'urgent',
    ResultCopy
  >;
  risk: {
    none: string;
    urgent: string;
  };
  dataSharing: {
    title: string;
    subtitle: string;
    statusOff: string;
    statusOn: string;
    pending: string;
    sendNow: string;
    setupTitle: string;
    setupHint: string;
    setupCodeLabel: string;
    consentTitle: string;
    consentBullets: string[];
    notSharedTitle: string;
    notSharedBullets: string[];
    mockNote: string;
    storageNote: string;
    consentButton: string;
    verifying: string;
    invalidCode: string;
    verifyFailed: string;
    disableButton: string;
    disableConfirm: string;
    deleteButton: string;
    deleteConfirm: string;
    deleteFailed: string;
    deleteDone: string;
    cancel: string;
    confirm: string;
    customizeSubtitle: string;
    openButton: string;
    contact: string;
  };
};

const en: AppCopy = {
  languageNames: {
    en: {label: 'English', helper: 'Available'},
    sw: {label: 'Swahili', helper: 'Prototype translation'},
    ts: {label: 'XiTsonga', helper: 'Prototype translation'},
    fr: {label: 'Français', helper: 'Prototype translation'},
  },
  landing: {
    eyebrow: 'Private self-check',
    subtitle:
      'A private self-check about stress, mood, worry, and daily life. It can help you understand what kind of support may help.',
    privacyTitle: 'Your answers are private.',
    privacyText:
      'We only ask questions needed for this self-check. You can pause or stop at any time.',
    chooseLanguage: 'Choose language',
    start: 'Self-check',
    continue: 'Continue self-check',
    providerView: 'Provider view',
    urgentHelp: 'Urgent help',
    learnMore: 'Learn more',
    credibilityCue: 'Research prototype',
    privacyReassurance:
      'You can review how the self-check works before answering, and choose what to share.',
    footer: 'This prototype is not a diagnosis and does not replace support from a trained person.',
  },
  accessibility: {
    title: 'Accessibility',
    subtitle: 'Adjust contrast and text size for this device.',
    highContrast: 'High contrast',
    highContrastOn: 'High contrast on',
    highContrastOff: 'High contrast off',
    textSize: 'Text size',
    textSizeStandard: 'Standard',
    textSizeLarge: 'Large',
    textSizeExtraLarge: 'Extra large',
    selected: 'Selected',
    selectLanguageHint: 'Changes the app language for ADAPT-MH interface text.',
    textSizeHint: 'Changes the size of ADAPT-MH interface text on this device.',
  },
  clinicianLanding: {
    tagline: 'MENTAL HEALTH & PSYCHOSOCIAL CARE',
    credibilityCue: 'Clinician prototype',
    screening: 'Screening',
    training: 'Training',
    analytics: 'Analytics',
    customize: 'Customize',
    resumeTitle: 'Continue screening?',
    resumeBody: 'A guided ADAPT-MH screening is saved on this device.',
    cancel: 'Cancel',
    startNew: 'Start new',
    continue: 'Continue',
  },
  clinicalPrivacy: {
    title: 'Before screening',
    subtitle:
      'ADAPT-MH asks one adaptive question at a time. Complete the screening together with the young person.',
    privateTitle: 'Clinician-guided screening',
    bullets: [
      'Explain that the questions cover stress, mood, worry, and daily life.',
      'Read each question aloud and record the answer the young person chooses.',
      'Use the back button to correct an answer, or pause the screening if a break is needed.',
      'Stop or pause if the young person becomes uncomfortable.',
    ],
    continue: 'Start screening',
    sharingNotice:
      'Research data sharing is on for site {site}: the questions, answers and results of this screening are stored pseudonymously on EPFL servers. Use a fictitious persona.',
  },
  clinicalOrientation: {
    title: 'Before screening',
    subtitle:
      'ADAPT-MH will ask one adaptive question at a time. The number of questions depends on the answers given.',
    bullets: [
      'Complete the screening together with the young person.',
      'Read each question aloud and record the answer they choose.',
      'Use the back button to correct an answer, or pause the screening if a break is needed.',
    ],
    start: 'Start screening',
  },
  privacy: {
    eyebrow: 'Privacy first',
    title: 'Before we begin',
    subtitle:
      'ADAPT-MH asks about stress, mood, worry, and daily life. Your answers help create a private self-check summary.',
    privateTitle: 'Your answers are private.',
    bullets: [
      'We only ask questions needed to understand how you are feeling.',
      'This prototype does not ask for your name, phone number, or address.',
      'You can pause or stop at any time.',
    ],
    note: 'This self-check is not a diagnosis. It is here to help you understand what kind of support may be useful.',
    continue: 'I understand',
  },
  orientation: {
    eyebrow: 'Self-check',
    title: 'The questions may change as you answer.',
    subtitle:
      'This helps ADAPT-MH ask only the questions that are most useful. There are no right or wrong answers.',
    bullets: [
      'Most people finish in about 10 minutes.',
      'You will see one question at a time.',
      'You can pause and come back on this device.',
      'Some questions may feel personal. Take your time.',
    ],
    privacy: 'Your answers are private. This tool is not here to judge you.',
    start: 'Start self-check',
  },
  questions: {
    title: 'Self-check',
    pauseTitle: 'Pause answering?',
    pauseMessage: 'Your answers can be saved on this device.',
    saveAndLeave: 'Save and leave',
    stay: 'Stay',
    restoring: 'Restoring your saved self-check...',
    recommending: 'Finding the next helpful question...',
    privacy: 'Your answers are private. You can pause at any time.',
    firstQuestion: 'First question',
    answered: 'answered',
    adaptive: 'Adaptive self-check',
    progressNote:
      'Your answers are private. The number of questions may change depending on your answers.',
    sensitiveTitle: 'Some questions may feel personal.',
    sensitiveBody: 'Take your time. We ask because your safety and wellbeing matter.',
    questionLabel: 'Question',
    continue: 'Continue',
    pause: 'Pause',
    enoughTitle: 'You have answered enough for this self-check.',
    enoughBody: 'ADAPT-MH has enough information to prepare private feedback.',
    noMoreQuestions: 'There are no more recommended questions right now.',
    finish: 'Finish self-check',
    loadingModels: 'Models are still loading. Please wait a moment and try again.',
    chooseAnswer: 'Please choose an answer before continuing.',
    invalidAnswer: 'This answer is invalid for the current question. Please choose again.',
    failedAnswer: 'Could not process this answer. Please try again.',
    savedTitle: 'Saved',
    savedBody: 'Your self-check is saved on this device.',
    backToStart: 'Back to start',
    urgentTitle: 'Please seek support now',
    urgentBody:
      'Your answers suggest you may need support urgently. You do not have to handle this alone.',
    seeSupport: 'See support options',
  },
  results: {
    eyebrow: 'Feedback',
    show: 'Show',
    hide: 'Hide',
    hiddenTitle: 'Your feedback is hidden.',
    hiddenBody: 'Tap Show when you are ready to view it again.',
    loading: 'Loading your feedback...',
    nextStep: 'Suggested next step',
    disclaimer:
      'This is a private self-check, not a formal diagnosis. A trained support person can help you understand what to do next.',
    getSupportNow: 'Get support now',
    seeSupport: 'See support options',
    backToStart: 'Back to start',
    clinical: {
      title: 'Results',
      conditionIndicators: 'Condition indicators',
      overallSeverity: 'Overall severity',
      clinicalNote: 'Clinical note',
      careGuidelines: 'Care Guidelines',
      advisory: 'Advisory only — not a diagnosis',
      indicatorLevels: {
        moderate: 'Moderate',
        mild: 'Mild',
        low: 'Low',
      },
      overallLevels: {
        doingOkay: 'Low',
        someStress: 'Mild',
        difficultTime: 'Mild–Moderate',
        extraSupport: 'Moderate',
        urgent: 'Urgent',
      },
      noteTemplates: {
        doingOkay:
          'No elevated condition indicators were identified. Continue routine wellbeing support.',
        someStress: 'Monitor {condition} and consider low-intensity support.',
        difficultTime: 'Review {condition} and arrange clinical follow-up.',
        extraSupport: 'Refer to care guidelines for {condition} and consider structured support.',
        urgent: 'Review urgent support guidance for {condition} and arrange immediate escalation.',
      },
    },
  },
  support: {
    eyebrow: 'Support',
    title: 'You do not have to handle this alone.',
    subtitle:
      'These options are placeholders for the local support pathways approved for each site.',
    urgentTitle: 'Please seek help now.',
    urgentBody:
      'If you feel unsafe or may hurt yourself, speak to a trusted adult, counsellor, nurse, or emergency support service now.',
    options: [
      {
        title: 'Talk to someone you trust',
        description:
          'A trusted adult, counsellor, teacher, nurse, or youth worker can help you decide what to do next.',
      },
      {
        title: 'Visit a local support service',
        description:
          'Use the local clinic, school counsellor, youth organisation, or community support pathway approved for your site.',
      },
      {
        title: 'Get urgent help now',
        description:
          'If you feel unsafe or may hurt yourself, please speak to a trusted adult, counsellor, nurse, or emergency support service now.',
        urgent: true,
      },
    ],
    backToStart: 'Back to start',
  },
  resultSummaries: {
    doing_okay: {
      headline: 'You seem to be doing okay right now.',
      explanation:
        'Your answers do not suggest strong signs of distress right now. Keep checking in with yourself and reach out if things change.',
      suggestedAction: 'Keep using the support habits and people that help you feel steady.',
    },
    some_stress: {
      headline: 'You may be under some stress.',
      explanation:
        'Your answers suggest some stress, worry, or low mood may be affecting daily life.',
      suggestedAction:
        'It could help to talk to someone you trust or try a small support step soon.',
    },
    difficult_time: {
      headline: 'You may be going through a difficult time.',
      explanation:
        'This does not mean something is wrong with you. It means support may make things easier to manage.',
      suggestedAction:
        'Consider speaking with a trusted adult, counsellor, nurse, or youth worker.',
    },
    extra_support: {
      headline: 'Your answers suggest you may need extra support soon.',
      explanation:
        'You may be dealing with stress, low mood, or worry that could be easier to handle with support.',
      suggestedAction:
        'Please reach out to a trusted person or local support service when you can.',
    },
    urgent: {
      headline: 'Your answers suggest you may need support urgently.',
      explanation:
        'You do not have to handle this alone. Please speak to a trusted adult, counsellor, nurse, or emergency support service now.',
      suggestedAction:
        'Get support now. If you are in immediate danger, contact emergency help in your area.',
    },
  },
  risk: {
    none: 'No urgent risk flag has been triggered.',
    urgent:
      'Your answers suggest you may need support urgently. You do not have to handle this alone.',
  },
  dataSharing: {
    title: 'Research data sharing',
    subtitle:
      'Share pseudonymised usage data from this device with the ADAPT-MH research team at EPFL. Off unless a site coordinator sets it up.',
    statusOff: 'Sharing is off. Nothing leaves this device.',
    statusOn: 'Sharing is on for site {site} since {date}.',
    pending: '{count} records waiting to be sent.',
    sendNow: 'Send now',
    setupTitle: 'Set up with your site code',
    setupHint:
      'Your site coordinator gives you a setup code, for example CH-GVA.xxxx. Type it exactly as received.',
    setupCodeLabel: 'Setup code',
    consentTitle: 'What is shared',
    consentBullets: [
      'Usage events: when a screening starts, pauses, resumes or completes, which screens are opened, the app language and version.',
      'Each screening: the questions asked, the answers chosen, and the computed results.',
      'Crash reports: the error message and app version, without any screening content.',
    ],
    notSharedTitle: 'What is never shared',
    notSharedBullets: [
      'Names, email addresses, phone numbers or any contact details.',
      'Participant aliases and provider notes.',
      'IP address, location or device identifiers.',
    ],
    mockNote:
      'In this phase only staff take part, using fictitious personas. Do not answer about yourself or a real person.',
    storageNote:
      'Data is stored on EPFL servers in Switzerland under a random installation number, kept for 12 months after the pilot ends, then reduced to aggregate statistics. You can withdraw or delete it at any time from this screen.',
    consentButton: 'I agree, turn on sharing',
    verifying: 'Checking the code…',
    invalidCode: 'This setup code is not valid. Check it with your site coordinator.',
    verifyFailed: 'Could not reach the research server. Check the connection and try again.',
    disableButton: 'Turn off sharing',
    disableConfirm: 'Stop sharing new data? Data already sent stays stored unless you delete it.',
    deleteButton: 'Delete my shared data',
    deleteConfirm:
      'Delete everything sent from this device and turn off sharing? This cannot be undone.',
    deleteFailed: 'Could not reach the research server. Check the connection and try again.',
    deleteDone: 'Deleted {sessions} screenings, {events} events and {crashes} crash reports.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    customizeSubtitle: 'Site-specific configuration for this device.',
    openButton: 'Research data sharing',
    contact: 'Questions or requests about your shared data: write to the project team at',
  },
};

const sw: AppCopy = {
  ...en,
  languageNames: {
    en: {label: 'English', helper: 'Inapatikana'},
    sw: {label: 'Swahili', helper: 'Tafsiri ya mfano'},
    ts: {label: 'XiTsonga', helper: 'Tafsiri ya mfano'},
    fr: {label: 'Français', helper: 'Tafsiri ya mfano'},
  },
  landing: {
    eyebrow: 'Kujichunguza kwa faragha',
    subtitle:
      'Kujichunguza kwa faragha kuhusu msongo, hali ya moyo, wasiwasi, na maisha ya kila siku. Inaweza kukusaidia kuelewa aina ya msaada unaoweza kufaa.',
    privacyTitle: 'Majibu yako ni ya faragha.',
    privacyText:
      'Tunauliza tu maswali yanayohitajika kwa kujichunguza huku. Unaweza kusitisha au kuacha wakati wowote.',
    chooseLanguage: 'Chagua lugha',
    start: 'Kujichunguza',
    continue: 'Endelea kujichunguza',
    providerView: 'Mwonekano wa mhudumu',
    urgentHelp: 'Msaada wa haraka',
    learnMore: 'Jifunze zaidi',
    credibilityCue: 'Mfano wa utafiti',
    privacyReassurance:
      'Unaweza kuona jinsi kujichunguza kunavyofanya kazi kabla ya kujibu, na kuchagua unachotaka kushiriki.',
    footer:
      'Mfano huu si utambuzi wa kitabibu na hauchukui nafasi ya msaada kutoka kwa mtu aliyefunzwa.',
  },
  accessibility: {
    title: 'Ufikikaji',
    subtitle: 'Rekebisha utofautishaji wa rangi na ukubwa wa maandishi kwenye kifaa hiki.',
    highContrast: 'Utofautishaji mkubwa',
    highContrastOn: 'Utofautishaji mkubwa umewashwa',
    highContrastOff: 'Utofautishaji mkubwa umezimwa',
    textSize: 'Ukubwa wa maandishi',
    textSizeStandard: 'Kawaida',
    textSizeLarge: 'Kubwa',
    textSizeExtraLarge: 'Kubwa zaidi',
    selected: 'Imechaguliwa',
    selectLanguageHint: 'Hubadilisha lugha ya maandishi ya kiolesura cha ADAPT-MH.',
    textSizeHint: 'Hubadilisha ukubwa wa maandishi ya kiolesura cha ADAPT-MH kwenye kifaa hiki.',
  },
  clinicianLanding: {
    tagline: 'AFYA YA AKILI NA MSAADA WA KISAIKOLOJIA NA KIJAMII',
    credibilityCue: 'Mfano wa kitabibu',
    screening: 'Uchunguzi',
    training: 'Mafunzo',
    analytics: 'Takwimu',
    customize: 'Badilisha mipangilio',
    resumeTitle: 'Endelea na uchunguzi?',
    resumeBody: 'Uchunguzi wa ADAPT-MH unaoongozwa umehifadhiwa kwenye kifaa hiki.',
    cancel: 'Ghairi',
    startNew: 'Anza mpya',
    continue: 'Endelea',
  },
  clinicalPrivacy: {
    title: 'Kabla ya uchunguzi',
    subtitle:
      'ADAPT-MH huuliza swali moja linalojirekebisha kwa wakati. Kamilisha uchunguzi pamoja na kijana.',
    privateTitle: 'Uchunguzi unaoongozwa na mhudumu wa afya',
    bullets: [
      'Eleza kwamba maswali yanahusu msongo, hali ya moyo, wasiwasi, na maisha ya kila siku.',
      'Soma kila swali kwa sauti na urekodi jibu analolichagua kijana.',
      'Tumia kitufe cha kurudi nyuma kusahihisha jibu, au simamisha uchunguzi ikiwa mapumziko yanahitajika.',
      'Simamisha au sitisha ikiwa kijana anaanza kutojisikia vizuri.',
    ],
    continue: 'Anza uchunguzi',
    sharingNotice:
      'Kushiriki data za utafiti kumewashwa kwa kituo {site}: maswali, majibu na matokeo ya uchunguzi huu huhifadhiwa bila utambulisho kwenye seva za EPFL. Tumia mhusika wa kubuni.',
  },
  clinicalOrientation: {
    title: 'Kabla ya uchunguzi',
    subtitle:
      'ADAPT-MH itauliza swali moja linalojirekebisha kwa wakati. Idadi ya maswali inategemea majibu yanayotolewa.',
    bullets: [
      'Kamilisha uchunguzi pamoja na kijana.',
      'Soma kila swali kwa sauti na urekodi jibu wanalolichagua.',
      'Tumia kitufe cha kurudi nyuma kusahihisha jibu, au simamisha uchunguzi ikiwa mapumziko yanahitajika.',
    ],
    start: 'Anza uchunguzi',
  },
  privacy: {
    eyebrow: 'Faragha kwanza',
    title: 'Kabla hatujaanza',
    subtitle:
      'ADAPT-MH huuliza kuhusu msongo, hali ya moyo, wasiwasi, na maisha ya kila siku. Majibu yako husaidia kutengeneza muhtasari wa faragha.',
    privateTitle: 'Majibu yako ni ya faragha.',
    bullets: [
      'Tunauliza tu maswali yanayohitajika kuelewa unavyojisikia.',
      'Mfano huu hauulizi jina, nambari ya simu, au anwani yako.',
      'Unaweza kusitisha au kuacha wakati wowote.',
    ],
    note: 'Kujichunguza huku si utambuzi wa kitabibu. Kupo kukusaidia kuelewa aina ya msaada unaoweza kufaa.',
    continue: 'Nimeelewa',
  },
  orientation: {
    eyebrow: 'Kujichunguza',
    title: 'Maswali yanaweza kubadilika kadiri unavyojibu.',
    subtitle:
      'Hii husaidia ADAPT-MH kuuliza maswali yanayofaa zaidi. Hakuna majibu sahihi au yasiyo sahihi.',
    bullets: [
      'Watu wengi humaliza ndani ya takribani dakika 10.',
      'Utaona swali moja kwa wakati.',
      'Unaweza kusitisha na kurudi kwenye kifaa hiki.',
      'Baadhi ya maswali yanaweza kuhisi binafsi. Chukua muda wako.',
    ],
    privacy: 'Majibu yako ni ya faragha. Zana hii haipo kukuhukumu.',
    start: 'Anza kujichunguza',
  },
  questions: {
    ...en.questions,
    title: 'Kujichunguza',
    pauseTitle: 'Sitisha kujichunguza?',
    pauseMessage: 'Majibu yako yanaweza kuhifadhiwa kwenye kifaa hiki.',
    saveAndLeave: 'Hifadhi na uondoke',
    stay: 'Baki',
    restoring: 'Tunarudisha sehemu uliyohifadhi...',
    recommending: 'Tunatafuta swali linalofuata linaloweza kusaidia...',
    privacy: 'Majibu yako ni ya faragha. Unaweza kusitisha wakati wowote.',
    firstQuestion: 'Swali la kwanza',
    answered: 'yamejibiwa',
    adaptive: 'Kujichunguza kunakobadilika',
    progressNote:
      'Majibu yako ni ya faragha. Idadi ya maswali inaweza kubadilika kulingana na majibu yako.',
    sensitiveTitle: 'Baadhi ya maswali yanaweza kuhisi binafsi.',
    sensitiveBody: 'Chukua muda wako. Tunauliza kwa sababu usalama na ustawi wako ni muhimu.',
    questionLabel: 'Swali',
    continue: 'Endelea',
    pause: 'Sitisha',
    enoughTitle: 'Umejibu vya kutosha kwa kujichunguza huku.',
    enoughBody: 'ADAPT-MH ina taarifa za kutosha kuandaa mrejesho wa faragha.',
    noMoreQuestions: 'Hakuna maswali mengine yaliyopendekezwa kwa sasa.',
    finish: 'Maliza kujichunguza',
    loadingModels: 'Miundo bado inapakia. Tafadhali subiri kidogo kisha ujaribu tena.',
    chooseAnswer: 'Tafadhali chagua jibu kabla ya kuendelea.',
    invalidAnswer: 'Jibu hili si sahihi kwa swali hili. Tafadhali chagua tena.',
    failedAnswer: 'Haikuwezekana kuchakata jibu hili. Tafadhali jaribu tena.',
    savedTitle: 'Imehifadhiwa',
    savedBody: 'Kujichunguza kwako kumehifadhiwa kwenye kifaa hiki.',
    backToStart: 'Rudi mwanzo',
    urgentTitle: 'Tafadhali tafuta msaada sasa',
    urgentBody:
      'Majibu yako yanaonyesha unaweza kuhitaji msaada wa haraka. Huhitaji kushughulikia hili peke yako.',
    seeSupport: 'Tazama chaguo za msaada',
  },
  results: {
    ...en.results,
    eyebrow: 'Mrejesho',
    show: 'Onyesha',
    hide: 'Ficha',
    hiddenTitle: 'Mrejesho wako umefichwa.',
    hiddenBody: 'Bonyeza Onyesha ukiwa tayari kuuona tena.',
    loading: 'Tunapakia mrejesho wako...',
    nextStep: 'Hatua inayopendekezwa',
    disclaimer:
      'Huku ni kujichunguza kwa faragha, si utambuzi rasmi. Mtu aliyefunzwa anaweza kukusaidia kuelewa hatua inayofuata.',
    getSupportNow: 'Pata msaada sasa',
    seeSupport: 'Tazama chaguo za msaada',
    backToStart: 'Rudi mwanzo',
    clinical: {
      title: 'Matokeo',
      conditionIndicators: 'Viashiria vya hali',
      overallSeverity: 'Ukali wa jumla',
      clinicalNote: 'Dokezo la kitabibu',
      careGuidelines: 'Miongozo ya Huduma',
      advisory: 'Ushauri pekee — si utambuzi',
      indicatorLevels: {
        moderate: 'Wastani',
        mild: 'Kidogo',
        low: 'Chini',
      },
      overallLevels: {
        doingOkay: 'Chini',
        someStress: 'Kidogo',
        difficultTime: 'Kidogo–Wastani',
        extraSupport: 'Wastani',
        urgent: 'Haraka',
      },
      noteTemplates: {
        doingOkay:
          'Hakuna viashiria vya hali vilivyoinuka vilivyobainika. Endelea na msaada wa kawaida wa ustawi.',
        someStress: 'Fuatilia {condition} na uzingatie msaada wa kiwango cha chini.',
        difficultTime: 'Pitia {condition} na panga ufuatiliaji wa kitabibu.',
        extraSupport: 'Rejelea miongozo ya huduma kwa {condition} na uzingatie msaada uliopangwa.',
        urgent: 'Pitia mwongozo wa msaada wa haraka kwa {condition} na panga hatua ya haraka.',
      },
    },
  },
  support: {
    ...en.support,
    eyebrow: 'Msaada',
    title: 'Huhitaji kushughulikia hili peke yako.',
    subtitle:
      'Chaguo hizi ni nafasi za mfano kwa njia za msaada za eneo husika zitakazoidhinishwa.',
    urgentTitle: 'Tafadhali tafuta msaada sasa.',
    urgentBody:
      'Ikiwa hujisikii salama au unaweza kujidhuru, zungumza sasa na mtu mzima unayemwamini, mshauri, muuguzi, au huduma ya dharura.',
    options: [
      {
        title: 'Zungumza na mtu unayemwamini',
        description:
          'Mtu mzima unayemwamini, mshauri, mwalimu, muuguzi, au mfanyakazi wa vijana anaweza kukusaidia kuamua hatua inayofuata.',
      },
      {
        title: 'Tembelea huduma ya msaada iliyo karibu',
        description:
          'Tumia kliniki, mshauri wa shule, shirika la vijana, au njia ya msaada ya jamii iliyoidhinishwa kwa eneo lako.',
      },
      {
        title: 'Pata msaada wa haraka sasa',
        description:
          'Ikiwa hujisikii salama au unaweza kujidhuru, tafadhali zungumza sasa na mtu mzima unayemwamini, mshauri, muuguzi, au huduma ya dharura.',
        urgent: true,
      },
    ],
    backToStart: 'Rudi mwanzo',
  },
  resultSummaries: {
    doing_okay: {
      headline: 'Kwa sasa unaonekana kuwa sawa.',
      explanation:
        'Majibu yako hayaonyeshi dalili kubwa za msongo kwa sasa. Endelea kujichunguza na tafuta msaada mambo yakibadilika.',
      suggestedAction: 'Endelea kutumia tabia na watu wanaokusaidia kujisikia imara.',
    },
    some_stress: {
      headline: 'Unaweza kuwa chini ya msongo fulani.',
      explanation:
        'Majibu yako yanaonyesha msongo, wasiwasi, au hali ya chini inaweza kuathiri maisha ya kila siku.',
      suggestedAction:
        'Inaweza kusaidia kuzungumza na mtu unayemwamini au kujaribu hatua ndogo ya msaada hivi karibuni.',
    },
    difficult_time: {
      headline: 'Unaweza kuwa unapitia kipindi kigumu.',
      explanation:
        'Hii haimaanishi kuna kitu kibaya kwako. Inamaanisha msaada unaweza kufanya mambo yawe rahisi kushughulikia.',
      suggestedAction:
        'Fikiria kuzungumza na mtu mzima unayemwamini, mshauri, muuguzi, au mfanyakazi wa vijana.',
    },
    extra_support: {
      headline: 'Majibu yako yanaonyesha unaweza kuhitaji msaada zaidi hivi karibuni.',
      explanation:
        'Unaweza kuwa unakabiliana na msongo, hali ya chini, au wasiwasi ambao unaweza kuwa rahisi kushughulikia kwa msaada.',
      suggestedAction: 'Tafadhali mfikie mtu unayemwamini au huduma ya msaada ukiweza.',
    },
    urgent: {
      headline: 'Majibu yako yanaonyesha unaweza kuhitaji msaada wa haraka.',
      explanation:
        'Huhitaji kushughulikia hili peke yako. Tafadhali zungumza sasa na mtu mzima unayemwamini, mshauri, muuguzi, au huduma ya dharura.',
      suggestedAction:
        'Pata msaada sasa. Ikiwa uko kwenye hatari ya haraka, wasiliana na msaada wa dharura katika eneo lako.',
    },
  },
  risk: {
    none: 'Hakuna alama ya hatari ya haraka iliyotokea.',
    urgent:
      'Majibu yako yanaonyesha unaweza kuhitaji msaada wa haraka. Huhitaji kushughulikia hili peke yako.',
  },
  dataSharing: {
    title: 'Kushiriki data za utafiti',
    subtitle:
      'Shiriki data za matumizi zisizo na utambulisho kutoka kifaa hiki na timu ya utafiti ya ADAPT-MH katika EPFL. Imezimwa hadi mratibu wa kituo aiweke.',
    statusOff: 'Kushiriki kumezimwa. Hakuna kinachotoka kwenye kifaa hiki.',
    statusOn: 'Kushiriki kumewashwa kwa kituo {site} tangu {date}.',
    pending: 'Rekodi {count} zinasubiri kutumwa.',
    sendNow: 'Tuma sasa',
    setupTitle: 'Weka kwa kutumia msimbo wa kituo chako',
    setupHint:
      'Mratibu wa kituo chako anakupa msimbo wa usanidi, kwa mfano CH-GVA.xxxx. Uandike kama ulivyoupokea.',
    setupCodeLabel: 'Msimbo wa usanidi',
    consentTitle: 'Kinachoshirikiwa',
    consentBullets: [
      'Matukio ya matumizi: uchunguzi unapoanza, kusitishwa, kuendelea au kukamilika, skrini zinazofunguliwa, lugha na toleo la programu.',
      'Kila uchunguzi: maswali yaliyoulizwa, majibu yaliyochaguliwa, na matokeo yaliyokokotolewa.',
      'Ripoti za hitilafu: ujumbe wa kosa na toleo la programu, bila maudhui yoyote ya uchunguzi.',
    ],
    notSharedTitle: 'Kisichoshirikiwa kamwe',
    notSharedBullets: [
      'Majina, anwani za barua pepe, nambari za simu au maelezo yoyote ya mawasiliano.',
      'Majina ya utambulisho ya washiriki na maelezo ya mhudumu.',
      'Anwani ya IP, eneo au vitambulisho vya kifaa.',
    ],
    mockNote:
      'Katika awamu hii wafanyakazi pekee ndio wanashiriki, wakitumia wahusika wa kubuni. Usijibu kuhusu wewe mwenyewe au mtu halisi.',
    storageNote:
      'Data huhifadhiwa kwenye seva za EPFL nchini Uswisi chini ya nambari ya usakinishaji ya nasibu, huhifadhiwa kwa miezi 12 baada ya majaribio kumalizika, kisha hupunguzwa kuwa takwimu za jumla. Unaweza kujiondoa au kufuta data wakati wowote kutoka skrini hii.',
    consentButton: 'Nakubali, washa kushiriki',
    verifying: 'Inakagua msimbo…',
    invalidCode: 'Msimbo huu wa usanidi si sahihi. Uhakiki na mratibu wa kituo chako.',
    verifyFailed: 'Haikuweza kufikia seva ya utafiti. Angalia muunganisho na ujaribu tena.',
    disableButton: 'Zima kushiriki',
    disableConfirm:
      'Acha kushiriki data mpya? Data zilizokwisha tumwa zitabaki zimehifadhiwa isipokuwa uzifute.',
    deleteButton: 'Futa data zangu zilizoshirikiwa',
    deleteConfirm:
      'Futa kila kitu kilichotumwa kutoka kifaa hiki na uzime kushiriki? Hili haliwezi kutenduliwa.',
    deleteFailed: 'Haikuweza kufikia seva ya utafiti. Angalia muunganisho na ujaribu tena.',
    deleteDone: 'Imefuta uchunguzi {sessions}, matukio {events} na ripoti za hitilafu {crashes}.',
    cancel: 'Ghairi',
    confirm: 'Thibitisha',
    customizeSubtitle: 'Usanidi mahususi wa kituo kwa kifaa hiki.',
    openButton: 'Kushiriki data za utafiti',
    contact: 'Maswali au maombi kuhusu data zako zilizoshirikiwa: andikia timu ya mradi kupitia',
  },
};

const ts: AppCopy = {
  ...en,
  languageNames: {
    en: {label: 'English', helper: 'Ya kumeka'},
    sw: {label: 'Swahili', helper: 'Vuhundzuluxeri bya prototype'},
    ts: {label: 'XiTsonga', helper: 'Vuhundzuluxeri bya prototype'},
    fr: {label: 'Français', helper: 'Vuhundzuluxeri bya prototype'},
  },
  landing: {
    eyebrow: 'Ku tikambela loku nga ka xihundla',
    subtitle:
      'Ku tikambela loku nga ka xihundla hi ntshikilelo, matitwelo, ku vilela, na vusiku bya siku na siku. Swi nga ku pfuna ku twisisa leswaku i nseketelo wihi lowu nga pfunaka.',
    privacyTitle: 'Tinhlamulo ta wena i ta xihundla.',
    privacyText:
      'Hi vutisa ntsena swivutiso leswi lavekaka eka ku tikambela loku. U nga wisa kumbe u yima nkarhi wunwana na wunwana.',
    chooseLanguage: 'Hlawula ririmi',
    start: 'Ku tikambela',
    continue: 'Yisa ku tikambela e mahlweni',
    providerView: 'Vonelo ra mupfuni',
    urgentHelp: 'Mpfuno wa xihatla',
    learnMore: 'Dyondza swo tala',
    credibilityCue: 'Prototype ya ndzavisiso',
    privacyReassurance:
      'U nga vona ndlela leyi ku tikambela ku tirhaka ha yona u nga si hlamula, kutani u hlawula leswi u lavaka ku swi avelana.',
    footer:
      'Prototype leyi a hi ku kambela ka vutshunguri naswona a yi siveli nseketelo wa munhu loyi a leteriweke.',
  },
  accessibility: {
    title: 'Ku fikeleleka',
    subtitle: 'Cinca ku vonakala ka mihlovo na vukulu bya marito eka xitirhisiwa lexi.',
    highContrast: 'Ku hambana ka mihlovo loku tlakukeke',
    highContrastOn: 'Ku hambana ka mihlovo ku pfuriwile',
    highContrastOff: 'Ku hambana ka mihlovo ku pfariwile',
    textSize: 'Vukulu bya marito',
    textSizeStandard: 'Tolovelekile',
    textSizeLarge: 'Kulu',
    textSizeExtraLarge: 'Kulu ngopfu',
    selected: 'Swi hlawuriwile',
    selectLanguageHint: 'Swi cinca ririmi ra marito ya interface ya ADAPT-MH.',
    textSizeHint: 'Swi cinca vukulu bya marito ya interface ya ADAPT-MH eka xitirhisiwa lexi.',
  },
  clinicianLanding: {
    tagline: 'RIHANYO RA MIEHLEKETO NA NSEKETELO WA VANHU',
    credibilityCue: 'Prototype ya vutshunguri',
    screening: 'Nkambelo',
    training: 'Vuleteri',
    analytics: 'Tinhlayo',
    customize: 'Lulamisa',
    resumeTitle: 'Yisa nkambelo emahlweni?',
    resumeBody: 'Nkambelo wa ADAPT-MH lowu kongomisiwaka wu hlayisiwile eka xitirhisiwa lexi.',
    cancel: 'Tshika',
    startNew: 'Sungula lowuntshwa',
    continue: 'Yisa emahlweni',
  },
  clinicalPrivacy: {
    title: 'Loko nkambelo wu nga si sungula',
    subtitle: 'ADAPT-MH yi vutisa xivutiso xinwe hi nkarhi. Hetisa nkambelo swinwe na muntshwa.',
    privateTitle: 'Nkambelo lowu kongomisiwaka hi mutshunguri',
    bullets: [
      'Hlamusela leswaku swivutiso swi khumba ntshikilelo, matitwelo, ku vilela, na vusiku bya siku na siku.',
      'Hlaya xivutiso xinwana na xinwana hi rito ra le henhla kutani u tsala nhlamulo leyi muntshwa a yi hlawulaka.',
      'Tirhisa bathini ya ku tlhelela endzhaku ku lulamisa nhlamulo, kumbe u yimisa nkambelo loko ku laveka ku wisa.',
      'Yimisa kumbe u wisa loko muntshwa a sungula ku nga titwi kahle.',
    ],
    continue: 'Sungula nkambelo',
    sharingNotice:
      'Ku avelana data ya ndzavisiso ku pfuriwile eka ndhawu {site}: swivutiso, tinhlamulo na mbuyelo wa nkambelo lowu swi hlayisiwa handle ka vutivi bya munhu eka tiseva ta EPFL. Tirhisa munhu wo endleriwa.',
  },
  clinicalOrientation: {
    title: 'Loko nkambelo wu nga si sungula',
    subtitle:
      'ADAPT-MH yi ta vutisa xivutiso xinwe hi nkarhi. Nhlayo ya swivutiso yi ya hi tinhlamulo leti nyikiwaka.',
    bullets: [
      'Hetisa nkambelo swinwe na muntshwa.',
      'Hlaya xivutiso xinwana na xinwana hi rito ra le henhla kutani u tsala nhlamulo leyi hlawuriwaka.',
      'Tirhisa bathini ya ku tlhelela endzhaku ku lulamisa nhlamulo, kumbe u yimisa nkambelo loko ku laveka ku wisa.',
    ],
    start: 'Sungula nkambelo',
  },
  privacy: {
    eyebrow: 'Xihundla xi rhanga',
    title: 'Loko hi nga si sungula',
    subtitle:
      'ADAPT-MH yi vutisa hi ntshikilelo, matitwelo, ku vilela, na vusiku bya siku na siku. Tinhlamulo ta wena ti pfuna ku endla nkatsakanyo wa ku tikambela lowu nga ka xihundla.',
    privateTitle: 'Tinhlamulo ta wena i ta xihundla.',
    bullets: [
      'Hi vutisa ntsena swivutiso leswi lavekaka ku twisisa ndlela leyi u titwaka ha yona.',
      'Prototype leyi a yi vutisi vito ra wena, nomboro ya riqingho, kumbe adirese.',
      'U nga wisa kumbe u yima nkarhi wunwana na wunwana.',
    ],
    note: 'Ku tikambela loku a hi ku kambela ka vutshunguri. Ku endleriwe ku ku pfuna ku twisisa nseketelo lowu nga pfunaka.',
    continue: 'Ndza swi twisisa',
  },
  orientation: {
    eyebrow: 'Ku tikambela',
    title: 'Swivutiso swi nga cinca loko u ri karhi u hlamula.',
    subtitle:
      'Leswi swi pfuna ADAPT-MH ku vutisa swivutiso leswi nga pfuna ngopfu. A ku na tinhlamulo leti lulameke kumbe leti hoxeke.',
    bullets: [
      'Vanhu vo tala va heta kwalomu ka timinete ta 10.',
      'U ta vona xivutiso xinwe hi nkarhi.',
      'U nga wisa kutani u tlhela u vuya eka xitirhisiwa lexi.',
      'Swivutiso swinwana swi nga titwa swi ri swa munhu. Teka nkarhi wa wena.',
    ],
    privacy: 'Tinhlamulo ta wena i ta xihundla. Xitirhisiwa lexi a xi kona ku ku avanyisa.',
    start: 'Sungula ku tikambela',
  },
  questions: {
    ...en.questions,
    title: 'Ku tikambela',
    pauseTitle: 'Wisa ku tikambela?',
    pauseMessage: 'Tinhlamulo ta wena ti nga hlayisiwa eka xitirhisiwa lexi.',
    saveAndLeave: 'Hlayisa u huma',
    stay: 'Tshama',
    restoring: 'Hi vuyisa ku tikambela loku hlayisiweke...',
    recommending: 'Hi lava xivutiso lexi landzelaka lexi nga pfunaka...',
    privacy: 'Tinhlamulo ta wena i ta xihundla. U nga wisa nkarhi wunwana na wunwana.',
    firstQuestion: 'Xivutiso xo sungula',
    answered: 'swi hlamuriwile',
    adaptive: 'Ku tikambela loku cincaka',
    progressNote:
      'Tinhlamulo ta wena i ta xihundla. Nhlayo ya swivutiso yi nga cinca hi ku ya hi tinhlamulo ta wena.',
    sensitiveTitle: 'Swivutiso swinwana swi nga titwa swi ri swa munhu.',
    sensitiveBody:
      'Teka nkarhi wa wena. Hi vutisa hikuva vuhlayiseki na ku hanya kahle ka wena swa nkoka.',
    questionLabel: 'Xivutiso',
    continue: 'Yisa e mahlweni',
    pause: 'Wisa',
    enoughTitle: 'U hlamule leswi eneleke eka ku tikambela loku.',
    enoughBody: 'ADAPT-MH yi ni vuxokoxoko byo enela ku lulamisa mbuyelo wa xihundla.',
    noMoreQuestions: 'A ku na swivutiso swinwana leswi ringanyetiweke sweswi.',
    finish: 'Hetisa ku tikambela',
    loadingModels: 'Timodel ti ha layicha. Yimanyana kutani u ringeta nakambe.',
    chooseAnswer: 'Hlawula nhlamulo loko u nga si ya e mahlweni.',
    invalidAnswer: 'Nhlamulo leyi a yi fambelani na xivutiso lexi. Hlawula nakambe.',
    failedAnswer: 'A swi kotekanga ku tirhisa nhlamulo leyi. Ringeta nakambe.',
    savedTitle: 'Swi hlayisiwile',
    savedBody: 'Ku tikambela ka wena ku hlayisiwile eka xitirhisiwa lexi.',
    backToStart: 'Tlhela eku sunguleni',
    urgentTitle: 'Kombela nseketelo sweswi',
    urgentBody:
      'Tinhlamulo ta wena ti komba leswaku u nga lava nseketelo wa xihatla. A wu fanelanga ku langutana na leswi u ri wexe.',
    seeSupport: 'Vona tindlela ta nseketelo',
  },
  results: {
    ...en.results,
    eyebrow: 'Mbuyelo',
    show: 'Kombisa',
    hide: 'Fihla',
    hiddenTitle: 'Mbuyelo wa wena wu fihliwile.',
    hiddenBody: 'Tinya Kombisa loko u lunghekele ku wu vona nakambe.',
    loading: 'Hi layicha mbuyelo wa wena...',
    nextStep: 'Goza leri ringanyetiweke',
    disclaimer:
      'Leswi i ku tikambela ka xihundla, a hi ku kambela ka ximfumo. Munhu loyi a leteriweke a nga ku pfuna ku twisisa leswi u nga swi endla.',
    getSupportNow: 'Kuma nseketelo sweswi',
    seeSupport: 'Vona tindlela ta nseketelo',
    backToStart: 'Tlhela eku sunguleni',
    clinical: {
      title: 'Mbuyelo',
      conditionIndicators: 'Swikombiso swa xiyimo',
      overallSeverity: 'Vukulu bya xiyimo hinkwaxo',
      clinicalNote: 'Xitsundzuxo xa vutshunguri',
      careGuidelines: 'Swiletelo swa Nhlayiso',
      advisory: 'I xitsundzuxo ntsena — a hi ku kambela ka vutshunguri',
      indicatorLevels: {
        moderate: 'Exikari',
        mild: 'Nyana',
        low: 'Ehansi',
      },
      overallLevels: {
        doingOkay: 'Ehansi',
        someStress: 'Nyana',
        difficultTime: 'Nyana–Exikari',
        extraSupport: 'Exikari',
        urgent: 'Xihatla',
      },
      noteTemplates: {
        doingOkay:
          'A ku vonakanga swikombiso swa xiyimo leswi tlakukeke. Yisa nseketelo wa ntolovelo wa ku hanya kahle e mahlweni.',
        someStress: 'Landzelela {condition} naswona u anakanya hi nseketelo wa xiyimo xa le hansi.',
        difficultTime: 'Kambisisa {condition} kutani u hlela ku landzelerisa ka vutshunguri.',
        extraSupport:
          'Languta swiletelo swa nhlayiso swa {condition} naswona u anakanya hi nseketelo lowu hleriweke.',
        urgent:
          'Kambisisa swiletelo swa nseketelo wa xihatla swa {condition} kutani u hlela goza ra xihatla.',
      },
    },
  },
  support: {
    ...en.support,
    eyebrow: 'Nseketelo',
    title: 'A wu fanelanga ku langutana na leswi u ri wexe.',
    subtitle:
      'Tindlela leti i swikhomela-ndhawu swa tindlela ta nseketelo ta ndhawu leti nga ta tiyisisiwa.',
    urgentTitle: 'Kombela mpfuno sweswi.',
    urgentBody:
      'Loko u nga titwi u hlayisekile kumbe u nga tivavisa, vulavula sweswi na munhu lonkulu loyi u nwi tshembaka, mukhanselara, nese, kumbe vukorhokeri bya xihatla.',
    options: [
      {
        title: 'Vulavula na munhu loyi u nwi tshembaka',
        description:
          'Munhu lonkulu loyi u nwi tshembaka, mukhanselara, mudyondzisi, nese, kumbe mutirhi wa vantshwa a nga ku pfuna ku teka goza leri landzelaka.',
      },
      {
        title: 'Endzela vukorhokeri bya nseketelo bya le kusuhi',
        description:
          'Tirhisa kliniki, mukhanselara wa xikolo, nhlangano wa vantshwa, kumbe ndlela ya nseketelo wa muganga leyi tiyisisiweke.',
      },
      {
        title: 'Kuma mpfuno wa xihatla sweswi',
        description:
          'Loko u nga titwi u hlayisekile kumbe u nga tivavisa, kombela mpfuno wa munhu lonkulu loyi u nwi tshembaka, mukhanselara, nese, kumbe vukorhokeri bya xihatla sweswi.',
        urgent: true,
      },
    ],
    backToStart: 'Tlhela eku sunguleni',
  },
  resultSummaries: {
    doing_okay: {
      headline: 'Sweswi u vonaka u ri kahle.',
      explanation:
        'Tinhlamulo ta wena a ti kombi swikombiso lesikulu swa ku karhateka sweswi. Yisa ku tivona e mahlweni naswona kombela nseketelo loko swilo swi cinca.',
      suggestedAction: 'Yisa e mahlweni mikhuva na vanhu lava ku pfunaka ku titwa u tiyile.',
    },
    some_stress: {
      headline: 'U nga ha va u ri ehansi ka ntshikilelo wunwana.',
      explanation:
        'Tinhlamulo ta wena ti komba leswaku ntshikilelo, ku vilela, kumbe matitwelo ya le hansi swi nga khumba vusiku bya siku na siku.',
      suggestedAction:
        'Swi nga pfuna ku vulavula na munhu loyi u nwi tshembaka kumbe ku ringeta goza leritsongo ra nseketelo ku nga ri khale.',
    },
    difficult_time: {
      headline: 'U nga ha va u ri eka nkarhi wo tika.',
      explanation:
        'Leswi a swi vuli leswaku ku na leswi hoxeke eka wena. Swi vula leswaku nseketelo wu nga endla swilo swi olova ku swi langutana na swona.',
      suggestedAction:
        'Anakanya ku vulavula na munhu lonkulu loyi u nwi tshembaka, mukhanselara, nese, kumbe mutirhi wa vantshwa.',
    },
    extra_support: {
      headline: 'Tinhlamulo ta wena ti komba leswaku u nga lava nseketelo wunwana ku nga ri khale.',
      explanation:
        'U nga ha va u langutana na ntshikilelo, matitwelo ya le hansi, kumbe ku vilela loku nga olovaka ku ku khoma loko u ri na nseketelo.',
      suggestedAction:
        'Loko swi koteka, fika eka munhu loyi u nwi tshembaka kumbe vukorhokeri bya nseketelo.',
    },
    urgent: {
      headline: 'Tinhlamulo ta wena ti komba leswaku u nga lava nseketelo wa xihatla.',
      explanation:
        'A wu fanelanga ku langutana na leswi u ri wexe. Vulavula sweswi na munhu lonkulu loyi u nwi tshembaka, mukhanselara, nese, kumbe vukorhokeri bya xihatla.',
      suggestedAction:
        'Kuma nseketelo sweswi. Loko u ri ekhombyeni ra xihatla, tihlanganise na mpfuno wa xihatla wa ndhawu ya wena.',
    },
  },
  risk: {
    none: 'A ku na mfungho wa khombo ra xihatla lowu humeleleke.',
    urgent:
      'Tinhlamulo ta wena ti komba leswaku u nga lava nseketelo wa xihatla. A wu fanelanga ku langutana na leswi u ri wexe.',
  },
  dataSharing: {
    title: 'Ku avelana data ya ndzavisiso',
    subtitle:
      'Avelana data ya matirhiselo leyi nga riki na vutivi bya munhu ku suka eka xitirhisiwa lexi na ntlawa wa ndzavisiso wa ADAPT-MH eEPFL. Yi timiwile ku fikela loko mulawuri wa ndhawu a yi lulamisa.',
    statusOff: 'Ku avelana ku timiwile. A ku na nchumu lowu humaka eka xitirhisiwa lexi.',
    statusOn: 'Ku avelana ku pfuriwile eka ndhawu {site} ku sukela {date}.',
    pending: 'Tirhekhodo ta {count} ti rindzele ku rhumeriwa.',
    sendNow: 'Rhumela sweswi',
    setupTitle: 'Lulamisa hi khodi ya ndhawu ya wena',
    setupHint:
      'Mulawuri wa ndhawu ya wena u ku nyika khodi yo lulamisa, xikombiso CH-GVA.xxxx. Yi tsale hilaha u yi kumeke hakona.',
    setupCodeLabel: 'Khodi yo lulamisa',
    consentTitle: 'Leswi avelaniwaka',
    consentBullets: [
      'Swiendlakalo swa matirhiselo: loko nkambelo wu sungula, wu yimisiwa, wu ya emahlweni kumbe wu hela, swikirini leswi pfuriwaka, ririmi na vhexini ya app.',
      "Nkambelo wun'wana na wun'wana: swivutiso leswi vutisiweke, tinhlamulo leti hlawuriweke, na mbuyelo lowu hlayiweke.",
      'Swiviko swa ku wa ka app: rungula ra xihoxo na vhexini ya app, handle ka vundzeni bya nkambelo.',
    ],
    notSharedTitle: 'Leswi swi nga avelaniwiki',
    notSharedBullets: [
      "Mavito, tiadirese ta email, tinomboro ta riqingho kumbe vuxokoxoko byin'wana bya vuhlanganisi.",
      'Mavito yo tumbeta ya vatekaxiave na tinotsi ta muphakeri.',
      'Adirese ya IP, ndhawu kumbe swikombiso swa xitirhisiwa.',
    ],
    mockNote:
      "Eka xiyimo lexi ku nghenelela vatirhi ntsena, va tirhisa vanhu vo endleriwa. U nga hlamuli hi wena n'wini kumbe munhu wa xiviri.",
    storageNote:
      "Data yi hlayisiwa eka tiseva ta EPFL eSwitzerland ehansi ka nomboro yo tshamela ya ku nghenisa, yi hlayisiwa tin'hweti ta 12 endzhaku ka ku hela ka xiringanyeto, kutani yi hunguteriwa eka tinhlayo ta nkatsakanyo. U nga tsuvula kumbe u sula data nkarhi wihi na wihi ku suka eka xikirini lexi.",
    consentButton: 'Ndza pfumela, pfula ku avelana',
    verifying: 'Ku kamberiwa khodi…',
    invalidCode: 'Khodi leyi yo lulamisa a yi lulamanga. Yi kambele na mulawuri wa ndhawu ya wena.',
    verifyFailed:
      'A swi kotekanga ku fikelela seva ya ndzavisiso. Kambela vuhlanganisi u tlhela u ringeta.',
    disableButton: 'Tima ku avelana',
    disableConfirm:
      'Yimisa ku avelana data leyintshwa? Data leyi rhumeriweke yi tshama yi hlayisiwile handle ka loko u yi sula.',
    deleteButton: 'Sula data ya mina leyi avelaniweke',
    deleteConfirm:
      'Sula hinkwaswo leswi rhumeriweke ku suka eka xitirhisiwa lexi u tlhela u tima ku avelana? Leswi a swi nge tlheriseriwi endzhaku.',
    deleteFailed:
      'A swi kotekanga ku fikelela seva ya ndzavisiso. Kambela vuhlanganisi u tlhela u ringeta.',
    deleteDone:
      'Ku suriwe mikambelo ya {sessions}, swiendlakalo swa {events} na swiviko swa ku wa swa {crashes}.',
    cancel: 'Tshika',
    confirm: 'Tiyisisa',
    customizeSubtitle: 'Vulawuri bya ndhawu bya xitirhisiwa lexi.',
    openButton: 'Ku avelana data ya ndzavisiso',
    contact:
      'Swivutiso kumbe swikombelo mayelana na data ya wena leyi avelaniweke: tsalela ntlawa wa phurojeke eka',
  },
};

const fr: AppCopy = {
  ...en,
  languageNames: {
    en: {label: 'English', helper: 'Disponible'},
    sw: {label: 'Swahili', helper: 'Traduction prototype'},
    ts: {label: 'XiTsonga', helper: 'Traduction prototype'},
    fr: {label: 'Français', helper: 'Traduction prototype'},
  },
  landing: {
    eyebrow: 'Auto-évaluation privée',
    subtitle:
      "Une auto-évaluation privée sur le stress, le moral, les inquiétudes et la vie quotidienne. Elle peut t'aider à comprendre quel type de soutien pourrait te convenir.",
    privacyTitle: 'Tes réponses restent privées.',
    privacyText:
      'Nous posons uniquement les questions nécessaires à cette auto-évaluation. Tu peux faire une pause ou arrêter à tout moment.',
    chooseLanguage: 'Choisir la langue',
    start: 'Auto-évaluation',
    continue: "Reprendre l'auto-évaluation",
    providerView: 'Vue prestataire',
    urgentHelp: 'Aide urgente',
    learnMore: 'En savoir plus',
    credibilityCue: 'Prototype de recherche',
    privacyReassurance:
      "Tu peux voir comment fonctionne l'auto-évaluation avant de répondre, et choisir ce que tu veux partager.",
    footer:
      "Ce prototype n'est pas un diagnostic et ne remplace pas l'aide d'un(e) professionnel(le) ou d'une personne formée.",
  },
  accessibility: {
    title: 'Accessibilité',
    subtitle: 'Ajuste le contraste et la taille du texte pour cet appareil.',
    highContrast: 'Contraste élevé',
    highContrastOn: 'Contraste élevé activé',
    highContrastOff: 'Contraste élevé désactivé',
    textSize: 'Taille du texte',
    textSizeStandard: 'Standard',
    textSizeLarge: 'Grande',
    textSizeExtraLarge: 'Très grande',
    selected: 'Sélectionné',
    selectLanguageHint: "Change la langue de l'interface d'ADAPT-MH.",
    textSizeHint: "Change la taille du texte de l'interface d'ADAPT-MH sur cet appareil.",
  },
  clinicianLanding: {
    tagline: 'SANTÉ MENTALE ET SOUTIEN PSYCHOSOCIAL',
    credibilityCue: 'Prototype pour cliniciens',
    screening: 'Dépistage',
    training: 'Formation',
    analytics: 'Statistiques',
    customize: 'Personnaliser',
    resumeTitle: 'Continuer le dépistage ?',
    resumeBody: 'Un dépistage ADAPT-MH guidé est enregistré sur cet appareil.',
    cancel: 'Annuler',
    startNew: 'Nouveau dépistage',
    continue: 'Continuer',
  },
  clinicalPrivacy: {
    title: 'Avant le dépistage',
    subtitle:
      'ADAPT-MH pose une question adaptative à la fois. Réalisez le dépistage avec le ou la jeune.',
    privateTitle: 'Dépistage guidé par un(e) clinicien(ne)',
    bullets: [
      'Expliquez que les questions portent sur le stress, le moral, les inquiétudes et la vie quotidienne.',
      'Lisez chaque question à voix haute et enregistrez la réponse choisie par le ou la jeune.',
      'Utilisez le bouton retour pour corriger une réponse, ou interrompez le dépistage si une pause est nécessaire.',
      "Arrêtez ou faites une pause si le ou la jeune se sent mal à l'aise.",
    ],
    continue: 'Commencer le dépistage',
    sharingNotice:
      "Le partage des données de recherche est activé pour le site {site} : les questions, réponses et résultats de ce dépistage sont stockés de manière pseudonymisée sur des serveurs de l'EPFL. Utilisez un personnage fictif.",
  },
  clinicalOrientation: {
    title: 'Avant le dépistage',
    subtitle:
      'ADAPT-MH posera une question adaptative à la fois. Le nombre de questions dépend des réponses données.',
    bullets: [
      'Réalisez le dépistage avec le ou la jeune.',
      'Lisez chaque question à voix haute et enregistrez la réponse choisie.',
      'Utilisez le bouton retour pour corriger une réponse, ou interrompez le dépistage si une pause est nécessaire.',
    ],
    start: 'Commencer le dépistage',
  },
  privacy: {
    eyebrow: "La confidentialité d'abord",
    title: 'Avant de commencer',
    subtitle:
      'ADAPT-MH pose des questions sur le stress, le moral, les inquiétudes et la vie quotidienne. Tes réponses servent à créer un résumé privé de ton auto-évaluation.',
    privateTitle: 'Tes réponses restent privées.',
    bullets: [
      'Nous posons uniquement les questions nécessaires pour comprendre comment tu te sens.',
      'Ce prototype ne demande ni ton nom, ni ton numéro de téléphone, ni ton adresse.',
      'Tu peux faire une pause ou arrêter à tout moment.',
    ],
    note: "Cette auto-évaluation n'est pas un diagnostic. Elle est là pour t'aider à comprendre quel type de soutien pourrait être utile.",
    continue: "J'ai compris",
  },
  orientation: {
    eyebrow: 'Auto-évaluation',
    title: 'Les questions peuvent changer selon tes réponses.',
    subtitle:
      "Cela aide ADAPT-MH à poser uniquement les questions les plus utiles. Il n'y a pas de bonnes ou de mauvaises réponses.",
    bullets: [
      'La plupart des gens terminent en 10 minutes environ.',
      'Tu verras une question à la fois.',
      'Tu peux faire une pause et revenir sur cet appareil.',
      'Certaines questions peuvent sembler personnelles. Prends ton temps.',
    ],
    privacy: "Tes réponses restent privées. Cet outil n'est pas là pour te juger.",
    start: "Commencer l'auto-évaluation",
  },
  questions: {
    ...en.questions,
    title: 'Auto-évaluation',
    pauseTitle: 'Faire une pause ?',
    pauseMessage: 'Tes réponses peuvent être enregistrées sur cet appareil.',
    saveAndLeave: 'Enregistrer et quitter',
    stay: 'Rester',
    restoring: 'Restauration de ton auto-évaluation enregistrée...',
    recommending: 'Recherche de la prochaine question utile...',
    privacy: 'Tes réponses restent privées. Tu peux faire une pause à tout moment.',
    firstQuestion: 'Première question',
    answered: 'répondues',
    adaptive: 'Auto-évaluation adaptative',
    progressNote:
      'Tes réponses restent privées. Le nombre de questions peut changer selon tes réponses.',
    sensitiveTitle: 'Certaines questions peuvent sembler personnelles.',
    sensitiveBody:
      'Prends ton temps. Nous posons ces questions parce que ta sécurité et ton bien-être comptent.',
    questionLabel: 'Question',
    continue: 'Continuer',
    pause: 'Pause',
    enoughTitle: 'Tu as répondu à suffisamment de questions pour cette auto-évaluation.',
    enoughBody: "ADAPT-MH a assez d'informations pour préparer un bilan privé.",
    noMoreQuestions: "Il n'y a plus de question recommandée pour le moment.",
    finish: "Terminer l'auto-évaluation",
    loadingModels:
      'Les modèles sont encore en cours de chargement. Patiente un instant puis réessaie.',
    chooseAnswer: 'Choisis une réponse avant de continuer.',
    invalidAnswer: "Cette réponse n'est pas valide pour la question actuelle. Choisis à nouveau.",
    failedAnswer: 'Impossible de traiter cette réponse. Essaie à nouveau.',
    savedTitle: 'Enregistré',
    savedBody: 'Ton auto-évaluation est enregistrée sur cet appareil.',
    backToStart: "Retour à l'accueil",
    urgentTitle: "Demande de l'aide maintenant",
    urgentBody:
      "Tes réponses suggèrent que tu pourrais avoir besoin de soutien de toute urgence. Tu n'as pas à affronter cela seul(e).",
    seeSupport: 'Voir les options de soutien',
  },
  results: {
    ...en.results,
    eyebrow: 'Bilan',
    show: 'Afficher',
    hide: 'Masquer',
    hiddenTitle: 'Ton bilan est masqué.',
    hiddenBody: 'Appuie sur Afficher quand tu es prêt(e) à le revoir.',
    loading: 'Chargement de ton bilan...',
    nextStep: 'Prochaine étape suggérée',
    disclaimer:
      "Ceci est une auto-évaluation privée, pas un diagnostic. Un(e) professionnel(le) ou une personne formée peut t'aider à comprendre quoi faire ensuite.",
    getSupportNow: "Demander de l'aide maintenant",
    seeSupport: 'Voir les options de soutien',
    backToStart: "Retour à l'accueil",
    clinical: {
      title: 'Résultats',
      conditionIndicators: 'Indicateurs par trouble',
      overallSeverity: 'Sévérité globale',
      clinicalNote: 'Note clinique',
      careGuidelines: 'Recommandations de prise en charge',
      advisory: 'À titre indicatif seulement — pas un diagnostic',
      indicatorLevels: {
        moderate: 'Modéré',
        mild: 'Léger',
        low: 'Faible',
      },
      overallLevels: {
        doingOkay: 'Faible',
        someStress: 'Léger',
        difficultTime: 'Léger–Modéré',
        extraSupport: 'Modéré',
        urgent: 'Urgent',
      },
      noteTemplates: {
        doingOkay:
          "Aucun indicateur élevé n'a été identifié. Poursuivre le soutien psychosocial de base.",
        someStress:
          'Surveiller {condition} et envisager un soutien psychosocial de faible intensité.',
        difficultTime: 'Évaluer {condition} et organiser un suivi clinique.',
        extraSupport:
          'Consulter les recommandations de prise en charge pour {condition} et envisager un soutien structuré.',
        urgent:
          "Consulter les recommandations d'aide urgente pour {condition} et organiser une orientation immédiate vers une prise en charge spécialisée.",
      },
    },
  },
  support: {
    ...en.support,
    eyebrow: 'Soutien',
    title: "Tu n'as pas à affronter cela seul(e).",
    subtitle:
      'Ces options sont des exemples ; elles seront remplacées par les ressources de soutien locales approuvées pour chaque site.',
    urgentTitle: "Demande de l'aide maintenant.",
    urgentBody:
      "Si tu ne te sens pas en sécurité ou si tu risques de te faire du mal, parle tout de suite à un adulte de confiance, à un(e) conseiller(ère) psychosocial(e), à un(e) infirmier(ère) ou aux services d'urgence.",
    options: [
      {
        title: "Parle à quelqu'un en qui tu as confiance",
        description:
          "Un adulte de confiance, un(e) conseiller(ère) psychosocial(e), un(e) enseignant(e), un(e) infirmier(ère) ou un(e) animateur(trice) de jeunesse peut t'aider à décider quoi faire ensuite.",
      },
      {
        title: 'Rends-toi dans un service de soutien local',
        description:
          "Adresse-toi au centre de santé local, au conseiller ou à la conseillère scolaire, à l'organisation de jeunesse ou au parcours de soutien communautaire approuvé pour ton site.",
      },
      {
        title: 'Demande une aide urgente maintenant',
        description:
          "Si tu ne te sens pas en sécurité ou si tu risques de te faire du mal, parle tout de suite à un adulte de confiance, à un(e) conseiller(ère) psychosocial(e), à un(e) infirmier(ère) ou aux services d'urgence.",
        urgent: true,
      },
    ],
    backToStart: "Retour à l'accueil",
  },
  resultSummaries: {
    doing_okay: {
      headline: 'Tu sembles aller plutôt bien en ce moment.',
      explanation:
        'Tes réponses ne montrent pas de signes importants de détresse en ce moment. Continue à faire le point avec toi-même et demande du soutien si les choses changent.',
      suggestedAction:
        "Continue à t'appuyer sur les habitudes et les personnes qui t'aident à te sentir bien.",
    },
    some_stress: {
      headline: 'Tu vis peut-être un certain stress.',
      explanation:
        'Tes réponses suggèrent que du stress, des inquiétudes ou une baisse de moral pourraient affecter ta vie quotidienne.',
      suggestedAction:
        "Cela pourrait t'aider de parler à quelqu'un en qui tu as confiance ou de faire bientôt une première démarche pour obtenir du soutien.",
    },
    difficult_time: {
      headline: 'Tu traverses peut-être une période difficile.',
      explanation:
        "Cela ne veut pas dire que quelque chose ne va pas chez toi. Cela veut dire qu'un soutien pourrait rendre les choses plus faciles à gérer.",
      suggestedAction:
        'Envisage de parler à un adulte de confiance, à un(e) conseiller(ère) psychosocial(e), à un(e) infirmier(ère) ou à un(e) animateur(trice) de jeunesse.',
    },
    extra_support: {
      headline:
        'Tes réponses suggèrent que tu pourrais bientôt avoir besoin de soutien supplémentaire.',
      explanation:
        'Tu fais peut-être face à du stress, une baisse de moral ou des inquiétudes qui seraient plus faciles à gérer avec du soutien.',
      suggestedAction:
        'Quand tu le peux, contacte une personne de confiance ou un service de soutien local.',
    },
    urgent: {
      headline: 'Tes réponses suggèrent que tu pourrais avoir besoin de soutien de toute urgence.',
      explanation:
        "Tu n'as pas à affronter cela seul(e). Parle tout de suite à un adulte de confiance, à un(e) conseiller(ère) psychosocial(e), à un(e) infirmier(ère) ou aux services d'urgence.",
      suggestedAction:
        "Demande de l'aide maintenant. Si tu es en danger immédiat, contacte les services d'urgence de ta région.",
    },
  },
  risk: {
    none: "Aucune alerte de risque urgent n'a été déclenchée.",
    urgent:
      "Tes réponses suggèrent que tu pourrais avoir besoin de soutien de toute urgence. Tu n'as pas à affronter cela seul(e).",
  },
  dataSharing: {
    title: 'Partage des données de recherche',
    subtitle:
      "Partager des données d'utilisation pseudonymisées de cet appareil avec l'équipe de recherche ADAPT-MH de l'EPFL. Désactivé tant qu'un coordinateur ou une coordinatrice de site ne l'a pas configuré.",
    statusOff: 'Le partage est désactivé. Rien ne quitte cet appareil.',
    statusOn: 'Le partage est activé pour le site {site} depuis le {date}.',
    pending: "{count} enregistrements en attente d'envoi.",
    sendNow: 'Envoyer maintenant',
    setupTitle: 'Configurer avec votre code de site',
    setupHint:
      'Votre coordinateur ou coordinatrice de site vous remet un code de configuration, par exemple CH-GVA.xxxx. Saisissez-le exactement tel que reçu.',
    setupCodeLabel: 'Code de configuration',
    consentTitle: 'Ce qui est partagé',
    consentBullets: [
      "Événements d'utilisation : début, pause, reprise ou fin d'un dépistage, écrans ouverts, langue et version de l'application.",
      'Chaque dépistage : les questions posées, les réponses choisies et les résultats calculés.',
      "Rapports de plantage : le message d'erreur et la version de l'application, sans aucun contenu du dépistage.",
    ],
    notSharedTitle: "Ce qui n'est jamais partagé",
    notSharedBullets: [
      'Noms, adresses e-mail, numéros de téléphone ou toute coordonnée.',
      'Alias des participants et notes du prestataire.',
      "Adresse IP, localisation ou identifiants de l'appareil.",
    ],
    mockNote:
      "Dans cette phase, seul le personnel participe, avec des personnages fictifs. Ne répondez pas à propos de vous-même ni d'une personne réelle.",
    storageNote:
      "Les données sont stockées sur des serveurs de l'EPFL en Suisse sous un numéro d'installation aléatoire, conservées 12 mois après la fin du projet pilote, puis réduites à des statistiques agrégées. Vous pouvez retirer votre accord ou supprimer les données à tout moment depuis cet écran.",
    consentButton: "J'accepte, activer le partage",
    verifying: 'Vérification du code…',
    invalidCode:
      "Ce code de configuration n'est pas valide. Vérifiez-le auprès de votre coordinateur ou coordinatrice de site.",
    verifyFailed:
      'Impossible de joindre le serveur de recherche. Vérifiez la connexion et réessayez.',
    disableButton: 'Désactiver le partage',
    disableConfirm:
      'Arrêter le partage de nouvelles données ? Les données déjà envoyées restent stockées sauf si vous les supprimez.',
    deleteButton: 'Supprimer mes données partagées',
    deleteConfirm:
      'Supprimer tout ce qui a été envoyé depuis cet appareil et désactiver le partage ? Cette action est irréversible.',
    deleteFailed:
      'Impossible de joindre le serveur de recherche. Vérifiez la connexion et réessayez.',
    deleteDone:
      '{sessions} dépistages, {events} événements et {crashes} rapports de plantage supprimés.',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    customizeSubtitle: 'Configuration propre au site pour cet appareil.',
    openButton: 'Partage des données de recherche',
    contact:
      "Questions ou demandes concernant vos données partagées : écrivez à l'équipe du projet à",
  },
};

export const getCopy = (language: AppLanguageCode = 'en'): AppCopy => {
  if (language === 'sw') return sw;
  if (language === 'ts') return ts;
  if (language === 'fr') return fr;
  return en;
};
